import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendIcon from "@material-ui/icons/Send";
import PublishIcon from "@material-ui/icons/Publish";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import CampaignModal from "../../components/CampaignModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import planilhaExemplo from "../../assets/planilha.xlsx";
import { useCampaignStyles, ProgressBar, toneColor } from "../Campaigns/campaignShared";

const listNumbers = (l) => {
  const total = Number(l.contactsCount || 0);
  const valid = Number(l.validCount || 0);
  return { total, valid, invalid: Math.max(0, total - valid) };
};

const ContactLists = () => {
  const classes = useCampaignStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const [dialog, setDialog] = useState({ open: false, id: null });
  const [campaignFor, setCampaignFor] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, list: null });
  const [deleting, setDeleting] = useState(null);

  const reload = useCallback(() => {
    setLists([]);
    setPageNumber(1);
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    setLists([]);
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contact-lists/", { params: { searchParam, pageNumber } });
        setLists((prev) => {
          const next = pageNumber === 1 ? [] : [...prev];
          data.records.forEach((r) => {
            const i = next.findIndex((x) => x.id === r.id);
            if (i !== -1) next[i] = r;
            else next.push(r);
          });
          return next;
        });
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber, reloadKey]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-ContactList`, (data) => {
      if (data.action === "delete") setLists((prev) => prev.filter((l) => l.id !== +data.id));
      else reload();
    });
    return () => socket.disconnect();
  }, [reload]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const openList = (l, importNow) => history.push(`/contact-lists/${l.id}/contacts${importNow ? "?importar=1" : ""}`);

  const handleDelete = async () => {
    const l = deleting;
    setDeleting(null);
    try {
      await api.delete(`/contact-lists/${l.id}`);
      toast.success("Lista excluída");
      setLists((prev) => prev.filter((x) => x.id !== l.id));
      setCount((c) => Math.max(0, c - 1));
    } catch (err) {
      toastError(err);
    }
  };

  const updatedText = (l) => {
    const d = l.lastItemAt || l.updatedAt;
    return d ? `Atualizada em ${moment(d).format("DD/MM/YYYY")}` : "";
  };

  const contactsCell = (l) => {
    const n = listNumbers(l);
    if (n.total === 0) return <span className={classes.subtle}>Nenhum contato ainda</span>;
    const vPct = (n.valid / n.total) * 100;
    return (
      <div style={{ minWidth: 200 }}>
        <div style={{ fontSize: 13 }}>
          <b>{n.total.toLocaleString("pt-BR")}</b>{" "}
          <span className={classes.muted}>
            · {n.valid.toLocaleString("pt-BR")} com WhatsApp{n.invalid > 0 ? ` · ${n.invalid} sem` : ""}
          </span>
        </div>
        <ProgressBar
          classes={classes}
          segments={[
            { pct: vPct, color: toneColor(theme, "green") },
            { pct: 100 - vPct, color: toneColor(theme, "red") },
          ]}
        />
      </div>
    );
  };

  const usedText = (l) => {
    const n = Number(l.campaignsCount || 0);
    if (!n) return "Nenhuma";
    return `${n} ${n === 1 ? "campanha" : "campanhas"}`;
  };

  const menuItems = (l) =>
    l
      ? [
          { label: "Importar planilha", icon: <PublishIcon fontSize="small" />, onClick: () => openList(l, true), mobileOnly: true },
          { label: "Renomear", icon: <EditOutlinedIcon fontSize="small" />, onClick: () => setDialog({ open: true, id: l.id }) },
          { label: "Usar em nova campanha", icon: <SendIcon fontSize="small" />, onClick: () => setCampaignFor(l.id) },
          { label: "Excluir", danger: true, icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => setDeleting(l) },
        ].filter((it) => !it.mobileOnly || isMobile)
      : [];

  const downloadButton = (
    <Button
      variant="outlined"
      component="a"
      href={planilhaExemplo}
      download="planilha-modelo.xlsx"
      className={`${classes.headerButton} ${classes.secondaryButton}`}
      startIcon={<GetAppIcon />}
    >
      Planilha modelo
    </Button>
  );

  return (
    <MainContainer>
      <ContactListDialog
        open={dialog.open}
        contactListId={dialog.id}
        onClose={() => setDialog({ open: false, id: null })}
        onSaved={(data) => {
          if (!dialog.id && data?.id) openList(data, true);
          else reload();
        }}
      />
      <CampaignModal
        open={!!campaignFor}
        initialContactListId={campaignFor}
        onClose={() => setCampaignFor(null)}
        onSaved={() => history.push("/campaigns")}
      />

      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        {deleting && (
          <>
            <DialogTitle>Excluir a lista “{deleting.name}”?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" className={classes.muted}>
                Os {listNumbers(deleting).total} contatos da lista serão apagados. Os contatos do atendimento não são
                afetados.
              </Typography>
              {Number(deleting.activeCampaignsCount || 0) > 0 && (
                <Typography variant="body2" style={{ marginTop: 12, color: toneColor(theme, "red"), fontWeight: 600 }}>
                  Atenção: esta lista está em {deleting.activeCampaignsCount} campanha(s) programada(s) ou enviando agora.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleting(null)}>Voltar</Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                disableElevation
                style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}
              >
                Excluir lista
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Menu
        anchorEl={menu.anchor}
        open={!!menu.anchor}
        onClose={() => setMenu({ anchor: null, list: null })}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuItems(menu.list).map((it) => (
          <MenuItem
            key={it.label}
            onClick={() => {
              setMenu({ anchor: null, list: null });
              it.onClick();
            }}
            style={it.danger ? { color: theme.palette.error.main } : undefined}
          >
            <ListItemIcon style={it.danger ? { color: "inherit" } : undefined}>{it.icon}</ListItemIcon>
            <ListItemText primary={it.label} />
          </MenuItem>
        ))}
      </Menu>

      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <Title>
              Listas de contatos
              {count > 0 && <span style={{ fontSize: 15, fontWeight: 500, color: theme.palette.text.secondary }}> · {count}</span>}
            </Title>
            {!isMobile && (
              <Typography variant="body2" className={classes.muted}>
                Grupos de contatos para usar como público das campanhas.
              </Typography>
            )}
          </div>
          <div className={classes.headerButtons}>
            {!isMobile && downloadButton}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              startIcon={<AddIcon />}
              onClick={() => setDialog({ open: true, id: null })}
            >
              Nova lista
            </Button>
          </div>
        </div>

        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Buscar lista"
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Buscar lista" }}
            />
          </div>

          <div className={classes.tableWrapper} onScroll={handleScroll}>
            {isMobile ? (
              <div>
                {lists.map((l) => {
                  const n = listNumbers(l);
                  return (
                    <div key={l.id} className={classes.mobileRow}>
                      <div className={classes.mobileInfo} onClick={() => openList(l)}>
                        <div className={classes.mobileTitle}>{l.name}</div>
                        <div className={classes.subtle}>
                          {n.total} contatos · {n.valid} com WhatsApp · {usedText(l).toLowerCase()}
                        </div>
                      </div>
                      <IconButton aria-label="Ações da lista" onClick={(e) => setMenu({ anchor: e.currentTarget, list: l })}>
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Lista</TableCell>
                    <TableCell className={classes.headCell} style={{ width: 300 }}>Contatos</TableCell>
                    <TableCell className={classes.headCell}>Usada em</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lists.map((l) => (
                    <TableRow key={l.id} className={classes.row}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <button type="button" className={classes.campaignName} onClick={() => openList(l)}>
                          {l.name}
                        </button>
                        <div className={classes.subtle} style={{ marginTop: 2 }}>{updatedText(l)}</div>
                      </TableCell>
                      <TableCell>{contactsCell(l)}</TableCell>
                      <TableCell className={Number(l.campaignsCount) ? undefined : classes.muted} style={{ fontSize: 13 }}>
                        {usedText(l)}
                      </TableCell>
                      <TableCell align="right" style={{ paddingRight: 12 }}>
                        <div className={classes.actions} style={{ gap: 6 }}>
                          <Button size="small" variant="outlined" className={`${classes.rowButton} ${classes.rowButtonOutlined}`} onClick={() => openList(l)}>
                            Ver contatos
                          </Button>
                          <Button size="small" variant="outlined" className={`${classes.rowButton} ${classes.rowButtonOutlined}`} onClick={() => openList(l, true)}>
                            Importar
                          </Button>
                          <IconButton
                            size="small"
                            aria-label="Mais ações"
                            className={classes.iconAction}
                            onClick={(e) => setMenu({ anchor: e.currentTarget, list: l })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            )}
            {!loading && lists.length === 0 && (
              <div className={classes.emptyState}>
                <PeopleOutlineIcon className={classes.emptyIcon} />
                <Typography variant="body2">
                  {searchParam ? "Nenhuma lista com esse nome" : "Nenhuma lista ainda. Crie uma e importe sua planilha de contatos."}
                </Typography>
                {!searchParam && (
                  <Button color="primary" onClick={() => setDialog({ open: true, id: null })}>
                    Criar a primeira lista
                  </Button>
                )}
              </div>
            )}
          </div>

          {lists.length > 0 && (
            <div className={classes.footer}>
              Mostrando {lists.length} de {count} {count === 1 ? "lista" : "listas"}
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default ContactLists;
